import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";

const EMPTY = {
  key: "",
  name: "",
  tagline: "",
  description: "",
  credits_per_sample: 5,
  icon: "flask-conical",
  active: true,

  display_in: {
    public: true,
    jobs: true,
  },
};

export default function AdminModulesPage() {
  const [modules, setModules] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/modules?only_active=false").then(({ data }) => setModules(data));
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setEditing(m.key);

    setForm({
      ...m,
      display_in: m.display_in ?? {
        public: true,
        jobs: true,
      },
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/modules/${editing}`, form);
        toast.success("Module updated.");
      } else {
        await api.post("/admin/modules", form);
        toast.success("Module created.");
      }
      cancel(); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (key) => {
    if (!confirm("Delete this module?")) return;
    await api.delete(`/admin/modules/${key}`);
    await load();
  };

  if (!modules) return <LoadingScreen />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-3">
        {modules.map((m) => (
          <div key={m.key} className="sqit-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-gray-900">{m.name}</h3>
                  {!m.active && <span className="pill bg-gray-100 text-gray-600 border-gray-200 text-[10px]">INACTIVE</span>}
                </div>
                <p className="text-xs text-gray-500">{m.tagline}</p>
                <div className="mt-2 text-sm text-gray-700">
                  <strong>{m.credits_per_sample} credits</strong> per sample · <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{m.key}</code>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(m)} className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50">Edit</button>
                <button onClick={() => remove(m.key)} className="text-xs px-3 py-1.5 border rounded-md text-red-600 hover:bg-red-50">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="sqit-card p-6">
        <h3 className="font-display text-lg font-semibold text-gray-900">
          {editing ? "Edit module" : "New module"}
        </h3>
        <div className="mt-4 space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold text-gray-700">Key (lowercase, no spaces)</label>
            <input value={form.key} disabled={!!editing}
              onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100" />
          </div>
          {[
            ["name", "Name"], ["tagline", "Tagline"], ["icon", "Icon (lucide-react name)"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-gray-700">{label}</label>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold text-gray-700">Description</label>
            <textarea value={form.description} rows={3}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Credits per sample</label>
            <input type="number" min={1} value={form.credits_per_sample}
              onChange={(e) => setForm({ ...form, credits_per_sample: Number(e.target.value) })}
              className="mt-1 w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <div className="pt-2 border-t">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Display In
            </p>

            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={form.display_in?.public ?? true}
                onChange={(e) =>
                  setForm({
                    ...form,
                    display_in: {
                      ...form.display_in,
                      public: e.target.checked,
                    },
                  })
                }
              />
              Public Website
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.display_in?.jobs ?? true}
                onChange={(e) =>
                  setForm({
                    ...form,
                    display_in: {
                      ...form.display_in,
                      jobs: e.target.checked,
                    },
                  })
                }
              />
              Job Submission
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={save}
              disabled={saving || !form.name || !form.key}
              className="flex-1 bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </button>

            {editing && (
              <button
                onClick={cancel}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
